import { useEffect, useRef } from "react";
import { useAuth } from "@/features/auth/context/auth-context";
import { authApi } from "@/lib/api";
import { getMsUntilExpiry, isJwtExpiringSoon } from "@/lib/jwt-payload";
import { refreshTokenOnce } from "@/lib/auth-session";

/**
 * Renueva el JWT automáticamente antes de expiración y al volver a la pestaña.
 */
export const useTokenRefresh = () => {
  const { token, setToken } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!token) {
      clearTimer();
      return;
    }

    const scheduleRefresh = () => {
      clearTimer();
      const msUntil = getMsUntilExpiry(token);
      const delay = msUntil !== null && msUntil > 0 ? msUntil : 5 * 60 * 1000;

      timerRef.current = setTimeout(() => {
        void (async () => {
          const newToken = await refreshTokenOnce(() => authApi.refresh());
          if (newToken) {
            setToken(newToken);
          }
        })();
      }, delay);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !token) return;
      if (isJwtExpiringSoon(token)) {
        void (async () => {
          const newToken = await refreshTokenOnce(() => authApi.refresh());
          if (newToken) {
            setToken(newToken);
          }
        })();
      }
    };

    scheduleRefresh();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [token, setToken]);
};
